import { apiFetch } from './client';

export type ChangelogCategory =
  | 'Breaking'
  | 'Deprecated'
  | 'New Feature'
  | 'Enhancement'
  | 'Bugfix';

export interface ChangelogEntry {
  jiraKey: string;
  summary: string;
  category: ChangelogCategory;
  affectedEndpoints?: string;
  migrationNotes?: string;
  /** ISO date string — present only for Deprecated entries */
  removalDate?: string;
  resolvedAt: string;
  /**
   * Hidden from end users by an admin. Only ever present for developer+
   * callers — the public response omits these entries entirely, so the SPA
   * never has to be trusted to hide anything.
   */
  excluded?: boolean;
}

export interface ChangelogDocument {
  apiId: string;
  versionId: string;
  jiraReleaseId: string;
  refreshedAt: string;
  entries: ChangelogEntry[];
  /** Count of tickets in the Fix Version with no Changelog Category — a tagging
   *  gap. Returned by refresh only; not persisted in the published changelog. */
  skippedUncategorized?: number;
}

export const getChangelog = (apiId: string, versionId: string) =>
  apiFetch<ChangelogDocument>(
    `/api/v1/apis/${apiId}/versions/${encodeURIComponent(versionId)}/changelog`,
  );

export const refreshChangelog = (apiId: string, versionId: string) =>
  apiFetch<ChangelogDocument>(
    `/api/v1/apis/${apiId}/versions/${encodeURIComponent(versionId)}/changelog/refresh`,
    { method: 'POST' },
  );

/**
 * Hides a changelog entry from end users, or restores it. Admin only.
 *
 * Returns the full set of hidden keys for the version, so the caller can
 * reconcile rather than assume its optimistic update was the whole story.
 */
export const setChangelogExclusion = (
  apiId: string,
  versionId: string,
  jiraKey: string,
  excluded: boolean,
) =>
  apiFetch<{ apiId: string; versionId: string; excluded: string[] }>(
    `/api/v1/apis/${apiId}/versions/${encodeURIComponent(versionId)}/changelog/exclusions`,
    { method: 'POST', body: JSON.stringify({ jiraKey, excluded }) },
  );
